import { useMemo, useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { Task } from '../store/types';
import { newTask, saveTask, tasksOn } from '../store/planner';
import { longDate, parseYmd, today } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { ROTULO_CURTO, bandeira, type Feriado, type TipoFeriado } from '../lib/feriados';
import { nomePais, recarregar, situacao } from '../lib/feriadosMundo';
import { useFeriados } from './useFeriados';
import { Chip, Sheet } from './ui';
import { C, alpha } from './theme';

/** Título da tarefa criada a partir de um feriado (traduzido na hora de criar). */
const TITULO: Record<TipoFeriado, string> = {
  nacional: N_('Feriado nacional: {nome}'), estadual: N_('Feriado estadual: {nome}'), regional: N_('Feriado regional: {nome}'),
  municipal: N_('Feriado municipal: {nome}'), facultativo: N_('Ponto facultativo: {nome}'),
};
const COR: Record<TipoFeriado, string> = { nacional: '#EF4444', estadual: '#EF4444', regional: '#EF4444', municipal: '#EF4444', facultativo: '#F59E0B' };

export const tituloFeriado = (f: Pick<Feriado, 'tipo' | 'nome'>) => t(TITULO[f.tipo], { nome: t(f.nome) });

/** Já existe no dia uma tarefa com o mesmo título? */
const jaTem = (f: Feriado, tasks: Task[]) => {
  const titulo = tituloFeriado(f).trim().toLowerCase();
  return tasksOn(f.data, tasks).some(x => x.title.trim().toLowerCase() === titulo);
};

/** Folha com todos os feriados do ano dos países escolhidos em Ajustes. Com mais de um país, mostra a lista
 *  de países no alto; com um só, vai direto para ele. Toque num feriado para pô-lo no Planner. */
export function FeriadosSheet({ visible, date, tasks, cor, onClose, onIrPara }: {
  visible: boolean; date: string; tasks: Task[]; cor: string; onClose: () => void; onIrPara?: (d: string) => void;
}) {
  const ins = useSafeAreaInsets();
  const feriados = useFeriados();
  const [ano, setAno] = useState(parseYmd(date).getFullYear());
  const [aberto, setAberto] = useState(visible);
  const [pais, setPais] = useState(feriados.paises[0]);
  if (visible !== aberto) { setAberto(visible); if (visible) setAno(parseYmd(date).getFullYear()); }
  const paisAtual = feriados.paises.includes(pais) ? pais : feriados.paises[0];
  const sit = paisAtual === 'BR' ? 'ok' : situacao(paisAtual, ano);

  // o mesmo feriado em várias cidades no mesmo dia vira uma linha só (ex.: Corpus Christi)
  const linhas = useMemo(() => {
    const g: { f: Feriado; locais: string[] }[] = [];
    for (const f of feriados.doAno(ano)) {
      if (f.pais !== paisAtual) continue;
      const ja = g.find(x => x.f.data === f.data && x.f.nome === f.nome && x.f.tipo === f.tipo);
      if (ja) { if (!ja.locais.includes(f.local)) ja.locais.push(f.local); } else g.push({ f, locais: [f.local] });
    }
    return g;
  }, [feriados, ano, paisAtual]);
  const hoje = today();

  const tocar = (f: Feriado) => {
    const titulo = tituloFeriado(f);
    const quando = longDate(f.data);
    if (jaTem(f, tasks)) {
      Alert.alert(t('Já está no Planner'), t('"{titulo}" já foi adicionado em {data}.', { titulo, data: quando }), [
        { text: t('OK'), style: 'cancel' },
        ...(onIrPara ? [{ text: t('Ver o dia'), onPress: () => { onIrPara(f.data); onClose(); } }] : []),
      ]);
      return;
    }
    Alert.alert(t('Adicionar ao Planner?'), `${titulo}\n${quando}`, [
      { text: t('Cancelar'), style: 'cancel' },
      { text: t('Adicionar'), onPress: () => {
        if (jaTem(f, tasks)) return; // segurança contra toque duplo
        saveTask(newTask({
          title: titulo, emoji: '🎉', emojiManual: true, color: COR[f.tipo],
          start: f.data, end: null, time: null, energy: 0, alerts: { mode: 'none', before: [] },
        }));
      } },
    ]);
  };

  return (
    <Sheet visible={visible} onClose={onClose} title={t('Feriados')}>
      <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingBottom: 6 }}>
        <Pressable onPress={() => setAno(a => a - 1)} hitSlop={12} style={{ padding: 8 }}><Text style={{ color: C.sub, fontSize: 24 }}>‹</Text></Pressable>
        <Text style={{ flex: 1, textAlign: 'center', color: C.text, fontSize: 20, fontWeight: '800' }}>{ano}</Text>
        <Pressable onPress={() => setAno(a => a + 1)} hitSlop={12} style={{ padding: 8 }}><Text style={{ color: C.sub, fontSize: 24 }}>›</Text></Pressable>
      </View>
      {feriados.paises.length > 1 && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', paddingHorizontal: 16, gap: 8, marginBottom: 8 }}>
          {feriados.paises.map(p => <Chip key={p} small cor={cor} on={p === paisAtual} label={`${bandeira(p)} ${nomePais(p)}`} onPress={() => setPais(p)} />)}
        </View>
      )}
      <Text style={{ color: C.sub, fontSize: 12, textAlign: 'center', paddingHorizontal: 16, marginBottom: 8 }}>
        {t('Toque num feriado para adicioná-lo ao Planner.')}
      </Text>
      <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: ins.bottom + 40, gap: 8 }}>
        {sit === 'carregando' && !linhas.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 30 }}>{t('Carregando feriados...')}</Text>}
        {sit === 'erro' && !linhas.length && (
          <Pressable onPress={() => recarregar(paisAtual, ano)} style={{ alignItems: 'center', gap: 6, marginTop: 30 }}>
            <Text style={{ color: C.sub, textAlign: 'center' }}>{t('Não foi possível baixar os feriados deste país. Confira a internet.')}</Text>
            <Text style={{ color: cor, fontWeight: '700' }}>{t('Tentar de novo')}</Text>
          </Pressable>
        )}
        {sit === 'sem-dados' && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 30 }}>{t('Ainda não há feriados deste país para este ano.')}</Text>}
        {sit === 'ok' && !linhas.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 30 }}>{t('Nenhum feriado neste ano.')}</Text>}
        {linhas.map(({ f, locais }) => {
          const feito = jaTem(f, tasks);
          const passado = f.data < hoje;
          const local = f.tipo === 'estadual' || f.tipo === 'municipal' || f.tipo === 'regional' ? locais.map(l => t(l)).join(', ') : '';
          return (
            <Pressable key={f.data + f.tipo + f.nome} onPress={() => tocar(f)}
              style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 14,
                backgroundColor: pressed ? C.card2 : C.card, borderLeftWidth: 4, borderLeftColor: COR[f.tipo], opacity: passado ? 0.6 : 1 })}>
              <View style={{ flex: 1, gap: 2 }}>
                <Text style={{ color: C.text, fontSize: 14 }}>
                  <Text style={{ color: COR[f.tipo], fontWeight: '800' }}>{t(ROTULO_CURTO[f.tipo])}</Text>: {longDate(f.data)} · <Text style={{ fontWeight: '700' }}>{t(f.nome)}</Text>
                </Text>
                {!!local && <Text style={{ color: C.sub, fontSize: 12 }}>{local}</Text>}
              </View>
              {feito
                ? <View style={{ width: 26, height: 26, borderRadius: 13, backgroundColor: alpha(cor, 0.2), alignItems: 'center', justifyContent: 'center' }}>
                    <Text style={{ color: cor, fontWeight: '800' }}>✓</Text>
                  </View>
                : <Text style={{ color: C.mute, fontSize: 20 }}>+</Text>}
            </Pressable>
          );
        })}
      </ScrollView>
    </Sheet>
  );
}
